/**
 * Click-capture hook for drawing a new no-fly zone polygon.
 *
 * Mirrors useDemoWaypointPlacement's structure: while `nfzDrawMode === true`
 * and a dock is selected, the next map clicks append vertices to the in-
 * progress polygon (`nfzDrawVertices` in the store). The NoFlyZoneLayer
 * renders the live draft visualization in parallel.
 *
 * Keyboard:
 *   - Enter   → finalize (calls addNoFlyZone on the selected dock if ≥3
 *               vertices). Subsequent Phase-3 UI exposes a "Finish" button
 *               that calls the same finalize path for non-keyboard users.
 *   - Escape  → cancel (clears the draft, exits draw mode).
 *
 * The hook gates internally on nfzDrawMode + selectedDockStationId, so
 * mounting it on a panel that's only conditionally rendered is fine; the
 * effect simply early-exits when draw mode is off.
 */

import { useEffect } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';

export function useDemoNFZDraw(): void {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const nfzDrawMode = useDockDemoStore((s) => s.nfzDrawMode);
  const selectedDockStationId = useDockDemoStore((s) => s.selectedDockStationId);
  const appendNfzDrawVertex = useDockDemoStore((s) => s.appendNfzDrawVertex);
  const clearNfzDraft = useDockDemoStore((s) => s.clearNfzDraft);
  const addNoFlyZone = useDockDemoStore((s) => s.addNoFlyZone);

  useEffect(() => {
    if (!map || !isMapReady || !nfzDrawMode || !selectedDockStationId) return;

    const onClick = (e: MapMouseEvent) => {
      appendNfzDrawVertex(e.lngLat.lng, e.lngLat.lat);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Re-read the latest vertex array from the store — `nfzDrawVertices`
        // captured in this closure would be stale because the click handler
        // doesn't re-bind on every append.
        const verts = useDockDemoStore.getState().nfzDrawVertices;
        if (verts.length >= 3) {
          addNoFlyZone(selectedDockStationId, verts);
        }
        // <3 vertices: ignore Enter. Phase 3 UI surfaces a hint banner
        // explaining the minimum.
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearNfzDraft();
      }
    };

    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';
    map.on('click', onClick);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      map.off('click', onClick);
      window.removeEventListener('keydown', onKeyDown);
      canvas.style.cursor = previousCursor;
    };
  }, [
    map,
    isMapReady,
    nfzDrawMode,
    selectedDockStationId,
    appendNfzDrawVertex,
    clearNfzDraft,
    addNoFlyZone,
  ]);
}
